import { useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Box, styled } from '@mui/material'
import { Typography } from '@carto/meridian-ds/components'
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined'
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined'
import { IconButton } from '@carto/meridian-ds/components'
import { Video } from '../types'

const WORKSPACE_VIDEO_URL = 'https://app-gallery.cartocdn.com/onboarding_assets/workspace_video.webp'
const WORKSPACE_VIDEO_HOVER_URL = 'https://app-gallery.cartocdn.com/onboarding_assets/workspace_video_hover.webp'
const BUILDER_VIDEO_URL = 'https://app-gallery.cartocdn.com/onboarding_assets/builder_video.webp'
const BUILDER_VIDEO_HOVER_URL = 'https://app-gallery.cartocdn.com/onboarding_assets/builder_video_hover.webp'
const WORKFLOWS_VIDEO_URL = 'https://app-gallery.cartocdn.com/onboarding_assets/workflows_video.webp'
const WORKFLOWS_VIDEO_HOVER_URL = 'https://app-gallery.cartocdn.com/onboarding_assets/workflows_video_hover.webp'

const TypeSelectorContainer = styled('div')(() => ({
  display: 'flex',
  overflowX: 'auto',
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',

  '&::-webkit-scrollbar': {
    display: 'none'
  }
}))

const TypeSelectorItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flex: 'none',
  marginRight: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  backgroundColor: theme.palette.common.white,
  transition: theme.transitions.create('border-color', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.shortest
  }),
  '& img': {
    borderRadius: theme.shape.borderRadius
  }
}))

interface OnboardingChecklistVideosProps {
  onClickVideo: (video: Video) => void
  videoTypeSelected: Video
}
const VIDEO_COVERS = [
  {
    icon: <img src={WORKSPACE_VIDEO_URL} alt="" height="192px" />,
    iconHover: <img src={WORKSPACE_VIDEO_HOVER_URL} alt="" height="192px" />,
    type: Video.WORKSPACE
  },
  {
    icon: <img src={BUILDER_VIDEO_URL} alt="" height="192px" />,
    iconHover: <img src={BUILDER_VIDEO_HOVER_URL} alt="" height="192px" />,
    type: Video.BUILDER
  },
  {
    icon: <img src={WORKFLOWS_VIDEO_URL} alt="" height="192px" />,
    iconHover: <img src={WORKFLOWS_VIDEO_HOVER_URL} alt="" height="192px" />,
    type: Video.WORKFLOWS
  }
]

function OnboardingChecklistVideos({ onClickVideo, videoTypeSelected }: OnboardingChecklistVideosProps) {
  const [hoveredType, setHoveredType] = useState<Video | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const handleClickVideo = (type: Video) => {
    onClickVideo(type)
  }

  useEffect(() => {
    containerRef.current
      ?.querySelector('.selected')
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
  }, [videoTypeSelected])

  const slicePrev = () => {
    containerRef.current?.scrollBy({ left: -350, behavior: 'smooth' })
  }

  const slideNext = () => {
    containerRef.current?.scrollBy({ left: 350, behavior: 'smooth' })
  }

  return (
    <Box mx={2} mb={1.75}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1">
          <FormattedMessage id="home.onboarding.welcome.videos.title" />
        </Typography>
        <Box ml="auto">
          <IconButton icon={<ChevronLeftOutlinedIcon />} onClick={slicePrev} />
          <IconButton icon={<ChevronRightOutlinedIcon />} onClick={slideNext} />
        </Box>
      </Box>
      <TypeSelectorContainer ref={containerRef}>
        {VIDEO_COVERS.map((video) => {
          const selected = video.type === videoTypeSelected
          const isHovered = video.type === hoveredType

          return (
            <TypeSelectorItem
              key={video.type}
              onClick={() => handleClickVideo(video.type)}
              className={`${selected && 'selected'} `}
              onMouseEnter={() => setHoveredType(video.type)}
              onMouseLeave={() => setHoveredType(null)}
            >
              {isHovered ? video.iconHover : video.icon}
            </TypeSelectorItem>
          )
        })}
      </TypeSelectorContainer>
    </Box>
  )
}

export default OnboardingChecklistVideos
